import { uniq } from 'lodash'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import type ShiftConflicts from 'modules/shift_management/store/internal/collections/ShiftsConflicts'
import type RestDay from 'modules/shift_management/store/internal/models/RestDay'
import type { RestDayPayload } from 'modules/shift_management/store/internal/models/RestDay'
import type Shift from 'modules/shift_management/store/internal/models/Shift'
import type { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import type View from 'modules/shift_management/store/internal/models/View'

const trackDeleteRestDaysEvent = (
  restDays: RestDay[],
  employeeCount: number,
  shiftsConflicts: ShiftConflicts,
  from: string
) => {
  track(trackingEvents.shiftManagement.deleteRestDaySubmit, {
    number_of_impacted_employees: employeeCount,
    number_of_impacted_rest_days: restDays.length,
    rest_days_ids: uniq(restDays.map((restDay) => restDay.id)),
    status_of_deleted_rest_days: restDays.map((restDay) => restDay.state),
    is_shift_conflict: restDays.map((restDay) => shiftsConflicts.has(restDay.id)),
    from: from,
  })
}

const trackDeleteShiftsEvent = ({
  shifts,
  view,
  employeeCount,
  shiftsConflicts,
  from,
  isBackup,
}: {
  shifts: Shift[]
  view: View
  employeeCount: number
  shiftsConflicts: ShiftConflicts
  from: string
  isBackup?: boolean
}) => {
  track(trackingEvents.shiftManagement.deleteShiftSubmit, {
    number_of_impacted_employees: employeeCount,
    number_of_impacted_shifts: shifts.length,
    shifts_ids: uniq(shifts.map((shift) => shift.id)),
    status_of_deleted_shifts: shifts.map((s) => s.state),
    is_shift_conflict: shifts.map((shift) => shiftsConflicts.has(shift.id)),
    view: view.viewType,
    view_type_by: view.groupType,
    from,
    is_backup: isBackup,
  })
}

const trackDeleteByRangeEvent = (
  shifts: ShiftPayload[],
  restDays: RestDayPayload[],
  view: View,
  startDateParam: string,
  endDateParam: string
) => {
  // Can't use ShiftManagementCounts since we are dealing with payloads and not with Shift/RestDay models.
  const shiftsCount = shifts.length
  const restDaysCount = restDays.length
  const shiftEmployeeIds = shifts.map((s) => s.employee_id)
  const restDayEmployeeIds = restDays.map((d) => d.employee_id)
  const employeeCount = uniq([...shiftEmployeeIds, ...restDayEmployeeIds]).length

  track(trackingEvents.shiftManagement.deleteShiftSubmit, {
    number_of_impacted_employees: employeeCount,
    number_of_impacted_shifts: shiftsCount,
    number_of_impacted_rest_days: restDaysCount,
    shifts_ids: shifts.map((s: ShiftPayload) => s.id),
    rest_day_ids: restDays.map((d: RestDayPayload) => d.id),
    status_of_deleted_shifts: shifts.map((s: ShiftPayload) => s.state),
    status_of_deleted_rest_days: restDays.map((d: RestDayPayload) => d.state),
    is_shift_conflict: shifts.map((s: ShiftPayload) => s.conflict),
    from_date: startDateParam,
    to_date: endDateParam,
    view: view.viewType,
    view_type_by: view.groupType,
  })
}

const trackBulkDeleteEvent = (
  shifts: Shift[],
  restDays: RestDay[],
  view: View,
  employeeCount: number,
  shiftsConflicts: ShiftConflicts,
  from: string
) => {
  track(trackingEvents.shiftManagement.deleteShiftSubmit, {
    number_of_impacted_employees: employeeCount,
    number_of_impacted_shifts: shifts.length,
    number_of_impacted_rest_days: restDays.length,
    shifts_ids: uniq(shifts.map((shift) => shift.id)),
    rest_days_ids: uniq(restDays.map((restDay) => restDay.id)),
    status_of_deleted_shifts: shifts.map((s) => s.state),
    status_of_deleted_rest_days: restDays.map((d) => d.state),
    is_shift_conflict: shifts.map((shift) => shiftsConflicts.has(shift.id)),
    view: view.viewType,
    view_type_by: view.groupType,
    from: from,
  })
}

export {
  trackBulkDeleteEvent,
  trackDeleteByRangeEvent,
  trackDeleteRestDaysEvent,
  trackDeleteShiftsEvent,
}
